import React, { useState, useEffect, useCallback, useRef } from 'react';
import { GoogleMap, useLoadScript, MarkerF, PolylineF, InfoWindowF } from '@react-google-maps/api';
import { collection, addDoc, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../../services/firebase';

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';
const LIBRARIES: ('places')[] = ['places'];

const ROUTE_COLORS = ['#E53935', '#1E88E5', '#43A047', '#FB8C00', '#8E24AA', '#00ACC1', '#D81B60', '#6D4C41', '#546E7A', '#F4511E'];

const MAP_STYLE = { width: '100%', height: '480px', borderRadius: '8px' };
const DEFAULT_CENTER = { lat: 28.6139, lng: 77.2090 }; // Delhi

interface StudentRouteAssignmentProps {
  domain: string;
}

interface StopCoord {
  name: string;
  lat: number;
  lng: number;
}

interface TransportRoute {
  id: string;
  routeName: string;
  routeNumber: string;
  startPoint: string;
  endPoint: string;
  stops: string[];
  stopCoordinates?: StopCoord[];
  assignedVehicle: string;
  assignedDriver: string;
  status: string;
  totalDistance: string;
}

interface Student {
  id: string;
  name: string;
  customUserId: string;
  className: string;
  section: string;
  address: string;
  city: string;
}

interface NearestStop {
  stopName: string;
  routeName: string;
  routeNumber: string;
  routeId: string;
  vehicleNumber: string;
  distance: number;
  stopLat: number;
  stopLng: number;
}

// Haversine distance in km
function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const StudentRouteAssignment: React.FC<StudentRouteAssignmentProps> = ({ domain }) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
  });

  const [routes, setRoutes] = useState<TransportRoute[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [assignedStudentIds, setAssignedStudentIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [geocodingRoutes, setGeocodingRoutes] = useState(false);
  const [geocodeProgress, setGeocodeProgress] = useState('');

  // Map state
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
  const [selectedStop, setSelectedStop] = useState<{ stop: StopCoord; routeName: string; routeNumber: string; color: string } | null>(null);
  const [studentMarker, setStudentMarker] = useState<{ lat: number; lng: number } | null>(null);

  // Assignment state
  const [selectedStudent, setSelectedStudent] = useState('');
  const [studentAddress, setStudentAddress] = useState('');
  const [nearestStops, setNearestStops] = useState<NearestStop[]>([]);
  const [searching, setSearching] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [assignSuccess, setAssignSuccess] = useState('');

  const mapRef = useRef<google.maps.Map | null>(null);
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    geocoderRef.current = new google.maps.Geocoder();
  }, []);

  // Load data
  useEffect(() => {
    if (!domain) return;
    loadData();
  }, [domain]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load routes
      const routesSnap = await getDocs(collection(db, `school-users/${domain}/transport-routes`));
      const routeList: TransportRoute[] = routesSnap.docs.map(d => ({
        id: d.id,
        ...d.data(),
        stops: d.data().stops || [],
        stopCoordinates: d.data().stopCoordinates || [],
      } as TransportRoute));
      setRoutes(routeList);

      // Auto-center map on first route with coordinates
      const firstWithCoords = routeList.find(r => r.stopCoordinates && r.stopCoordinates.length > 0);
      if (firstWithCoords && firstWithCoords.stopCoordinates!.length > 0) {
        setMapCenter({ lat: firstWithCoords.stopCoordinates![0].lat, lng: firstWithCoords.stopCoordinates![0].lng });
      }

      // Load students
      const studentsSnap = await getDocs(collection(db, `school-users/${domain}/students`));
      const studentList: Student[] = studentsSnap.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          name: data.name || data.studentName || '',
          customUserId: data.customUserId || data.enrollmentId || d.id,
          className: data.class || data.className || '',
          section: data.section || '',
          address: data.address || '',
          city: data.city || '',
        };
      });
      setStudents(studentList);

      // Load existing assignments to know who's already assigned
      const assignSnap = await getDocs(collection(db, `school-users/${domain}/student-transport`));
      const assignedIds = new Set<string>();
      assignSnap.docs.forEach(d => {
        const data = d.data();
        if (data.studentId) assignedIds.add(data.studentId);
      });
      setAssignedStudentIds(assignedIds);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Geocode all route stops
  const geocodeAllRoutes = async () => {
    if (!geocoderRef.current) return;
    setGeocodingRoutes(true);

    const geocoder = geocoderRef.current;

    for (const route of routes) {
      if (route.stopCoordinates && route.stopCoordinates.length === route.stops.length) {
        continue; // Already geocoded
      }

      const coords: StopCoord[] = [];
      // Geocode startPoint + stops + endPoint
      const allPoints = [route.startPoint, ...route.stops, route.endPoint].filter(Boolean);

      for (let i = 0; i < allPoints.length; i++) {
        const pointName = allPoints[i];
        setGeocodeProgress(`Geocoding: ${route.routeNumber} - ${pointName}`);

        try {
          const result = await new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
            geocoder.geocode({ address: pointName }, (results, status) => {
              if (status === 'OK' && results) resolve(results);
              else reject(new Error(`Geocode failed for "${pointName}": ${status}`));
            });
          });

          coords.push({
            name: pointName,
            lat: result[0].geometry.location.lat(),
            lng: result[0].geometry.location.lng(),
          });
        } catch (err) {
          console.warn(err);
          // Skip failed geocodes
        }

        // Rate limit: Google allows ~50 requests/sec but be safe
        await new Promise(r => setTimeout(r, 300));
      }

      if (coords.length > 0) {
        // Save to Firestore
        const routeRef = doc(db, `school-users/${domain}/transport-routes`, route.id);
        await updateDoc(routeRef, { stopCoordinates: coords });
      }
    }

    setGeocodeProgress('');
    setGeocodingRoutes(false);
    // Reload data
    loadData();
  };

  // Geocode a student address and find nearest stops
  const findNearestStops = async () => {
    if (!geocoderRef.current || !studentAddress.trim()) {
      alert('Please enter a student address');
      return;
    }

    setSearching(true);
    setNearestStops([]);
    setStudentMarker(null);
    setAssignSuccess('');

    try {
      const result = await new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
        geocoderRef.current!.geocode({ address: studentAddress }, (results, status) => {
          if (status === 'OK' && results) resolve(results);
          else reject(new Error(`Could not geocode address: ${status}`));
        });
      });

      const studentLat = result[0].geometry.location.lat();
      const studentLng = result[0].geometry.location.lng();
      setStudentMarker({ lat: studentLat, lng: studentLng });

      // Find nearest stops across all routes
      const allStops: NearestStop[] = [];

      routes.forEach((route) => {
        if (!route.stopCoordinates || route.stopCoordinates.length === 0) return;
        if (route.status !== 'Active') return;

        route.stopCoordinates.forEach((stop) => {
          const dist = haversineKm(studentLat, studentLng, stop.lat, stop.lng);
          allStops.push({
            stopName: stop.name,
            routeName: route.routeName,
            routeNumber: route.routeNumber,
            routeId: route.id,
            vehicleNumber: route.assignedVehicle || 'Not assigned',
            distance: Math.round(dist * 100) / 100,
            stopLat: stop.lat,
            stopLng: stop.lng,
          });
        });
      });

      // Sort by distance, take top 5
      allStops.sort((a, b) => a.distance - b.distance);
      setNearestStops(allStops.slice(0, 5));

      // Center map to show student and nearest stop
      if (allStops.length > 0 && mapRef.current) {
        const bounds = new google.maps.LatLngBounds();
        bounds.extend({ lat: studentLat, lng: studentLng });
        bounds.extend({ lat: allStops[0].stopLat, lng: allStops[0].stopLng });
        mapRef.current.fitBounds(bounds, 100);
      } else {
        setMapCenter({ lat: studentLat, lng: studentLng });
      }
    } catch (error: any) {
      console.error('Geocoding error:', error);
      alert(`Failed to find address: ${error.message}`);
    } finally {
      setSearching(false);
    }
  };

  // Assign student to a route/stop
  const assignStudent = async (stop: NearestStop) => {
    const student = students.find(s => s.customUserId === selectedStudent || s.id === selectedStudent);
    if (!student) {
      alert('Please select a student first');
      return;
    }

    setAssigning(true);
    try {
      await addDoc(collection(db, `school-users/${domain}/student-transport`), {
        studentId: student.customUserId,
        studentName: student.name,
        class: student.className ? `${student.className}${student.section ? `-${student.section}` : ''}` : '',
        routeNumber: stop.routeNumber,
        routeName: stop.routeName,
        pickupPoint: stop.stopName,
        dropPoint: stop.stopName,
        vehicleNumber: stop.vehicleNumber,
        transportType: 'Two-way',
        status: 'Active',
      });

      setAssignedStudentIds(prev => new Set([...prev, student.customUserId]));
      setAssignSuccess(`${student.name} assigned to ${stop.routeNumber} (${stop.stopName})`);
      setNearestStops([]);
      setStudentMarker(null);
      setSelectedStudent('');
      setStudentAddress('');
    } catch (error: any) {
      console.error('Error assigning student:', error);
      alert(`Failed to assign: ${error.message}`);
    } finally {
      setAssigning(false);
    }
  };

  // When a student is selected, auto-fill address
  const handleStudentSelect = (studentId: string) => {
    setSelectedStudent(studentId);
    setAssignSuccess('');
    setNearestStops([]);
    setStudentMarker(null);
    const student = students.find(s => s.customUserId === studentId || s.id === studentId);
    if (student) {
      const addr = [student.address, student.city].filter(Boolean).join(', ');
      setStudentAddress(addr);
    }
  };

  const unassignedStudents = students.filter(s => !assignedStudentIds.has(s.customUserId));
  const routesWithCoords = routes.filter(r => r.stopCoordinates && r.stopCoordinates.length > 0);
  const routesNeedingGeocode = routes.filter(r => !r.stopCoordinates || r.stopCoordinates.length === 0);

  // No API key fallback
  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div>
        <div className="alert alert-warning">
          <h5 className="alert-heading">
            <i className="bi bi-exclamation-triangle me-2"></i>
            Google Maps API Key Required
          </h5>
          <p className="mb-2">
            To use map-based route assignment, add your Google Maps API key to the environment:
          </p>
          <code className="d-block bg-light p-2 rounded mb-2">VITE_GOOGLE_MAPS_API_KEY=your_api_key_here</code>
          <small className="text-muted">
            Add this to your <code>.env</code> file and restart the dev server. You need the
            Maps JavaScript API and Geocoding API enabled in your Google Cloud Console.
          </small>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="alert alert-danger">
        <i className="bi bi-exclamation-circle me-2"></i>
        Failed to load Google Maps. Check your API key and network connection.
      </div>
    );
  }

  if (!isLoaded || loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status"></div>
        <p className="mt-2 text-muted">Loading map and data...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Stats */}
      <div className="row g-3 mb-4">
        <div className="col-6 col-md-3">
          <div className="card border-0 shadow-sm text-center">
            <div className="card-body py-2">
              <i className="bi bi-map fs-4 text-primary"></i>
              <h5 className="mb-0 mt-1">{routes.filter(r => r.status === 'Active').length}</h5>
              <small className="text-muted">Active Routes</small>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card border-0 shadow-sm text-center">
            <div className="card-body py-2">
              <i className="bi bi-geo-alt fs-4 text-success"></i>
              <h5 className="mb-0 mt-1">{routesWithCoords.length}</h5>
              <small className="text-muted">Mapped Routes</small>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card border-0 shadow-sm text-center">
            <div className="card-body py-2">
              <i className="bi bi-people fs-4 text-info"></i>
              <h5 className="mb-0 mt-1">{assignedStudentIds.size}</h5>
              <small className="text-muted">Assigned</small>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card border-0 shadow-sm text-center">
            <div className="card-body py-2">
              <i className="bi bi-person-exclamation fs-4 text-warning"></i>
              <h5 className="mb-0 mt-1">{unassignedStudents.length}</h5>
              <small className="text-muted">Unassigned</small>
            </div>
          </div>
        </div>
      </div>

      {/* Geocode Banner */}
      {routesNeedingGeocode.length > 0 && (
        <div className="alert alert-info d-flex align-items-center mb-4">
          <i className="bi bi-info-circle fs-4 me-3"></i>
          <div className="flex-grow-1">
            <strong>{routesNeedingGeocode.length} route(s)</strong> need to be mapped with coordinates.
            Click "Map Routes" to geocode all route stops automatically.
            {geocodeProgress && <small className="d-block text-muted mt-1">{geocodeProgress}</small>}
          </div>
          <button
            className="btn btn-primary btn-sm"
            onClick={geocodeAllRoutes}
            disabled={geocodingRoutes}
          >
            {geocodingRoutes ? (
              <><span className="spinner-border spinner-border-sm me-2"></span>Mapping...</>
            ) : (
              <><i className="bi bi-geo-alt me-2"></i>Map Routes</>
            )}
          </button>
        </div>
      )}

      <div className="row g-4">
        {/* Map */}
        <div className="col-lg-8">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white py-2">
              <h6 className="mb-0">
                <i className="bi bi-map me-2 text-primary-custom"></i>
                Route Map
              </h6>
            </div>
            <div className="card-body p-0">
              <GoogleMap
                mapContainerStyle={MAP_STYLE}
                center={mapCenter}
                zoom={12}
                onLoad={onMapLoad}
                options={{ streetViewControl: false, mapTypeControl: false }}
              >
                {/* Route polylines and stop markers */}
                {routesWithCoords.map((route, routeIdx) => {
                  const color = ROUTE_COLORS[routeIdx % ROUTE_COLORS.length];
                  const coords = route.stopCoordinates!;

                  return (
                    <React.Fragment key={route.id}>
                      {/* Polyline connecting stops */}
                      <PolylineF
                        path={coords.map(c => ({ lat: c.lat, lng: c.lng }))}
                        options={{
                          strokeColor: color,
                          strokeOpacity: 0.8,
                          strokeWeight: 4,
                        }}
                      />
                      {/* Stop markers */}
                      {coords.map((stop, stopIdx) => (
                        <MarkerF
                          key={`${route.id}-${stopIdx}`}
                          position={{ lat: stop.lat, lng: stop.lng }}
                          label={{
                            text: `${stopIdx + 1}`,
                            color: '#fff',
                            fontSize: '11px',
                            fontWeight: 'bold',
                          }}
                          icon={{
                            path: google.maps.SymbolPath.CIRCLE,
                            fillColor: color,
                            fillOpacity: 1,
                            strokeColor: '#fff',
                            strokeWeight: 2,
                            scale: 12,
                          }}
                          onClick={() => setSelectedStop({ stop, routeName: route.routeName, routeNumber: route.routeNumber, color })}
                        />
                      ))}
                    </React.Fragment>
                  );
                })}

                {/* Student location marker */}
                {studentMarker && (
                  <MarkerF
                    position={studentMarker}
                    icon={{
                      path: google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,
                      fillColor: '#FF4081',
                      fillOpacity: 1,
                      strokeColor: '#fff',
                      strokeWeight: 2,
                      scale: 7,
                    }}
                    title="Student Location"
                  />
                )}

                {/* Info window for selected stop */}
                {selectedStop && (
                  <InfoWindowF
                    position={{ lat: selectedStop.stop.lat, lng: selectedStop.stop.lng }}
                    onCloseClick={() => setSelectedStop(null)}
                  >
                    <div style={{ minWidth: '150px' }}>
                      <strong style={{ color: selectedStop.color }}>{selectedStop.routeNumber}</strong>
                      <span className="ms-2">{selectedStop.routeName}</span>
                      <br />
                      <small>{selectedStop.stop.name}</small>
                    </div>
                  </InfoWindowF>
                )}
              </GoogleMap>
            </div>
            {/* Route Legend */}
            {routesWithCoords.length > 0 && (
              <div className="card-footer bg-white py-2">
                <div className="d-flex flex-wrap gap-3">
                  {routesWithCoords.map((route, idx) => (
                    <small key={route.id} className="d-flex align-items-center">
                      <span
                        className="d-inline-block rounded-circle me-1"
                        style={{ width: '12px', height: '12px', backgroundColor: ROUTE_COLORS[idx % ROUTE_COLORS.length] }}
                      ></span>
                      {route.routeNumber} - {route.routeName}
                    </small>
                  ))}
                  {studentMarker && (
                    <small className="d-flex align-items-center">
                      <span className="d-inline-block me-1" style={{ width: '12px', height: '12px', backgroundColor: '#FF4081', clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)' }}></span>
                      Student Location
                    </small>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Assignment Panel */}
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white py-2">
              <h6 className="mb-0">
                <i className="bi bi-person-plus me-2 text-primary-custom"></i>
                Assign Student to Bus
              </h6>
            </div>
            <div className="card-body">
              {assignSuccess && (
                <div className="alert alert-success py-2 small">
                  <i className="bi bi-check-circle me-2"></i>{assignSuccess}
                </div>
              )}

              {/* Select Student */}
              <div className="mb-3">
                <label className="form-label small fw-semibold">Select Student</label>
                <select
                  className="form-select form-select-sm"
                  value={selectedStudent}
                  onChange={(e) => handleStudentSelect(e.target.value)}
                >
                  <option value="">-- Select an unassigned student --</option>
                  {unassignedStudents.map((s) => (
                    <option key={s.id} value={s.customUserId}>
                      {s.name} ({s.customUserId}){s.className ? ` - Class ${s.className}` : ''}
                    </option>
                  ))}
                </select>
                {unassignedStudents.length === 0 && (
                  <small className="text-success">All students are assigned!</small>
                )}
              </div>

              {/* Address Input */}
              <div className="mb-3">
                <label className="form-label small fw-semibold">Student Address</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={studentAddress}
                  onChange={(e) => setStudentAddress(e.target.value)}
                  placeholder="Enter full address..."
                  onKeyDown={(e) => e.key === 'Enter' && findNearestStops()}
                />
                <small className="text-muted">Auto-filled from student data. Edit if needed.</small>
              </div>

              <button
                className="btn btn-primary btn-sm w-100 mb-3"
                onClick={findNearestStops}
                disabled={searching || !studentAddress.trim()}
              >
                {searching ? (
                  <><span className="spinner-border spinner-border-sm me-2"></span>Searching...</>
                ) : (
                  <><i className="bi bi-search me-2"></i>Find Nearest Bus Stop</>
                )}
              </button>

              {/* Nearest Stops Results */}
              {nearestStops.length > 0 && (
                <div>
                  <h6 className="small fw-semibold mb-2">
                    <i className="bi bi-geo-alt me-1 text-success"></i>
                    Nearest Stops
                  </h6>
                  <div className="list-group list-group-flush">
                    {nearestStops.map((stop, idx) => (
                      <div key={idx} className={`list-group-item px-0 py-2 ${idx === 0 ? 'border-start border-3 border-success ps-2' : ''}`}>
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <strong className="small">{stop.stopName}</strong>
                            <small className="d-block text-muted">
                              {stop.routeNumber} - {stop.routeName}
                            </small>
                            <small className="text-muted">
                              <i className="bi bi-truck me-1"></i>{stop.vehicleNumber}
                            </small>
                          </div>
                          <div className="text-end">
                            <span className={`badge ${idx === 0 ? 'bg-success' : 'bg-secondary'} mb-1`}>
                              {stop.distance} km
                            </span>
                            <br />
                            <button
                              className="btn btn-sm btn-outline-primary py-0 px-2"
                              style={{ fontSize: '0.7rem' }}
                              onClick={() => assignStudent(stop)}
                              disabled={assigning || !selectedStudent}
                            >
                              {assigning ? '...' : 'Assign'}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {!selectedStudent && (
                    <small className="text-danger mt-2 d-block">
                      <i className="bi bi-exclamation-circle me-1"></i>
                      Select a student first to assign
                    </small>
                  )}
                </div>
              )}

              {nearestStops.length === 0 && !searching && routesWithCoords.length === 0 && (
                <div className="text-center text-muted py-3">
                  <i className="bi bi-geo-alt fs-3 d-block mb-2"></i>
                  <small>Map your routes first to enable assignment</small>
                </div>
              )}
            </div>
          </div>

          {/* Route Summary */}
          <div className="card border-0 shadow-sm mt-3">
            <div className="card-header bg-white py-2">
              <h6 className="mb-0">
                <i className="bi bi-signpost-2 me-2 text-primary-custom"></i>
                Routes ({routes.length})
              </h6>
            </div>
            <div className="card-body p-0">
              <div className="list-group list-group-flush" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                {routes.map((route, idx) => {
                  const hasCords = route.stopCoordinates && route.stopCoordinates.length > 0;
                  return (
                    <div
                      key={route.id}
                      className="list-group-item py-2 px-3"
                      style={{ cursor: hasCords ? 'pointer' : 'default' }}
                      onClick={() => {
                        if (hasCords && route.stopCoordinates!.length > 0 && mapRef.current) {
                          const bounds = new google.maps.LatLngBounds();
                          route.stopCoordinates!.forEach(c => bounds.extend({ lat: c.lat, lng: c.lng }));
                          mapRef.current.fitBounds(bounds, 80);
                        }
                      }}
                    >
                      <div className="d-flex align-items-center">
                        <span
                          className="d-inline-block rounded-circle me-2"
                          style={{
                            width: '10px',
                            height: '10px',
                            backgroundColor: hasCords ? ROUTE_COLORS[idx % ROUTE_COLORS.length] : '#ccc',
                          }}
                        ></span>
                        <div className="flex-grow-1">
                          <small className="fw-semibold">{route.routeNumber}</small>
                          <small className="text-muted ms-2">{route.routeName}</small>
                        </div>
                        <span className={`badge ${route.status === 'Active' ? 'bg-success' : 'bg-secondary'}`} style={{ fontSize: '0.65rem' }}>
                          {route.stops.length} stops
                        </span>
                        {!hasCords && (
                          <i className="bi bi-exclamation-circle text-warning ms-1" title="Not mapped yet" style={{ fontSize: '0.75rem' }}></i>
                        )}
                      </div>
                    </div>
                  );
                })}
                {routes.length === 0 && (
                  <div className="text-center text-muted py-3">
                    <small>No routes defined. Add routes in the Routes tab first.</small>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentRouteAssignment;
